import { useSearchParams } from 'react-router-dom';

/**
 * Custom hook that provides navigation functions that preserve the session_id
 */
export const useSessionNavigation = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  /**
   * Navigate to a path while preserving the current session_id
   * @param {string} path - The path to navigate to
   * @returns {string} - The complete URL with session_id preserved
   */
  const getNavigationUrl = (path) => {
    if (!sessionId) {
      return path;
    }
    
    const separator = path.includes('?') ? '&' : '?';
    return `${path}${separator}session_id=${sessionId}`;
  };

  /**
   * Navigate to home while preserving session_id
   * @returns {string} - Home URL with session_id
   */
  const getHomeUrl = () => {
    return getNavigationUrl('/');
  };

  /**
   * Navigate to subject page while preserving session_id
   * @param {string} subjectId - The subject ID
   * @returns {string} - Subject URL with session_id
   */
  const getSubjectUrl = (subjectId) => {
    return getNavigationUrl(`/subject/${subjectId}`);
  };

  /**
   * Navigate to question page while preserving session_id
   * @param {string} subjectId - The subject ID
   * @param {string|number} questionId - The question ID
   * @returns {string} - Question URL with session_id
   */
  const getQuestionUrl = (subjectId, questionId) => {
    return getNavigationUrl(`/subject/${subjectId}/question/${questionId}`);
  };

  return {
    sessionId,
    getNavigationUrl,
    getHomeUrl,
    getSubjectUrl,
    getQuestionUrl
  };
};

/**
 * Higher-order component that provides session-aware navigation
 * @param {Function} navigate - React Router navigate function
 * @param {string} sessionId - Current session ID
 */
export const createSessionNavigator = (navigate, sessionId) => {
  const navigateWithSession = (path) => {
    if (!sessionId) {
      navigate(path);
      return;
    }
    
    const separator = path.includes('?') ? '&' : '?';
    navigate(`${path}${separator}session_id=${sessionId}`);
  };

  return {
    navigate: navigateWithSession,
    navigateToHome: () => navigateWithSession('/'),
    navigateToSubject: (subjectId) => navigateWithSession(`/subject/${subjectId}`),
    navigateToQuestion: (subjectId, questionId) => navigateWithSession(`/subject/${subjectId}/question/${questionId}`)
  };
};

export default useSessionNavigation;
